import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import React, { ReactElement, useMemo } from 'react';
import './Report.css';
import { GoodsDto, OrderDto, WaybillFullInfoDto } from '../../api/ClientApi';
import { useAppSelector } from '../../hooks/hooks';
import { generateWaybillNumber } from '../../common/Extensions';
import { FormatDateText } from '../../common/datesHelper';
import { translateClientField } from '../companyDetails/CompanyDetailsHelper';

const makeSignatureElement = (title: string): ReactElement => {
    return (
        <>
            <span
                style={{
                    width: '100%',
                    height: 0,
                    display: 'inline-block',
                    borderBottom: '1px solid black',
                }}
            ></span>

            <Typography variant="caption" component="p" style={{ marginTop: '5px', textAlign: 'center' }}>
                {title}
            </Typography>
        </>
    );
};

const makeSignatureStampElement = (): ReactElement => {
    return (
        <div style={{ width: '100%' }}>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    width: '100%',
                }}
            >
                <div style={{ width: '33%' }}>{makeSignatureElement('Robu izdao')}</div>
                <div style={{ width: '33%' }}>{makeSignatureElement('Robu preuzeo')}</div>
            </div>
        </div>
    );
};

type WaybillReportProps = {
    waybillInfo: WaybillFullInfoDto | undefined;
    isInternalWaybill: boolean;
};

const WaybillReportContent = React.forwardRef(
    (props: WaybillReportProps, ref: React.Ref<HTMLDivElement> | undefined): ReactElement => {
        const { waybillInfo, isInternalWaybill } = props;
        const waybill = useMemo(() => {
            return waybillInfo?.waybill;
        }, [waybillInfo]);
        const order = useMemo(() => {
            return waybillInfo?.order;
        }, [waybillInfo]);
        const originSite = useMemo(() => {
            return waybillInfo?.originSite;
        }, [waybillInfo]);
        const destinationSite = useMemo(() => {
            return waybillInfo?.destinationSite;
        }, [waybillInfo]);
        const goods = useMemo(() => {
            return waybillInfo?.goods;
        }, [waybillInfo]);

        const companyDetails = useAppSelector((state) => state.companyDetails.companyDetails);
        const documentCompanyDetails = useAppSelector((state) => state.companyDetails.docCompDetails);
        const documentReceiverTemplate = useAppSelector((state) => state.companyDetails.docRecTemplate);

        const pageTitle = (): ReactElement => {
            return isInternalWaybill ? (
                <div className="waybill-report-content">
                    <div
                        style={{
                            width: '100%',
                            margin: 0,
                        }}
                    >
                        {companyDetails?.name && (
                            <Typography
                                key={companyDetails?.name}
                                variant="h4"
                                style={{ textAlign: 'end', margin: '0px', color: 'black' }}
                            >
                                {companyDetails?.name}
                            </Typography>
                        )}
                        {documentCompanyDetails?.map((dcd) => {
                            return (
                                <Typography
                                    key={dcd.index + dcd.label + dcd.value}
                                    variant="body2"
                                    style={{ textAlign: 'end', margin: '0px', color: 'black' }}
                                >
                                    {(dcd.label ?? '') + ': ' + (dcd.value ?? '')}
                                </Typography>
                            );
                        })}
                    </div>
                    <Box style={{ margin: 25 }} />

                    <div>
                        <Typography variant="body2" style={{ textAlign: 'start', margin: '0px', color: 'black' }}>
                            Broj: {generateWaybillNumber()}
                        </Typography>
                    </div>

                    <div>
                        <Typography variant="body2" style={{ textAlign: 'start', margin: '0px', color: 'black' }}>
                            Datum: {FormatDateText(new Date())}
                        </Typography>
                    </div>

                    <Typography
                        variant="h3"
                        style={{
                            textAlign: 'center',
                            margin: '0px',
                            color: 'black',
                            marginTop: 15,
                            marginBottom: 15,
                        }}
                    >
                        OTPREMNICA
                    </Typography>
                    <TableContainer>
                        <Table size="small" stickyHeader aria-label="simple table">
                            <TableBody>
                                <TableRow>
                                    <TableCell align="left">Vozilo:</TableCell>
                                    <TableCell align="left">{waybill?.vehicleLicensePlateNum ?? ''}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell align="left">Vozač:</TableCell>
                                    <TableCell align="left">{waybill?.driverName ?? ''}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell align="left">JMBG:</TableCell>
                                    <TableCell align="left">{waybill?.driverUniqueNumber ?? ''}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell align="left">Iz magacina:</TableCell>
                                    <TableCell align="left">
                                        {originSite?.name +
                                            (originSite?.locationInfo?.address
                                                ? ', ' + originSite?.locationInfo?.address
                                                : '')}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell align="left">Na lokaciju:</TableCell>
                                    <TableCell align="left">
                                        {destinationSite?.name +
                                            (destinationSite?.locationInfo?.address
                                                ? ', ' + destinationSite?.locationInfo?.address
                                                : '')}
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Typography
                        variant="h6"
                        style={{
                            textAlign: 'center',
                            margin: '0px',
                            marginTop: '4%',
                        }}
                    >
                        SPISAK ROBE
                    </Typography>
                </div>
            ) : (
                <div className="waybill-report-content">
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 50 }}>
                        <div
                            style={{
                                width: '100%',
                                margin: 0,
                            }}
                        >
                            <Typography variant="h6" style={{ textAlign: 'start', margin: '0px', color: 'black' }}>
                                Izdavalac
                            </Typography>
                            <hr />
                            {companyDetails?.name && (
                                <Typography
                                    key={companyDetails?.name}
                                    variant="h4"
                                    style={{ textAlign: 'start', margin: '0px', color: 'black' }}
                                >
                                    {companyDetails?.name}
                                </Typography>
                            )}
                            {documentCompanyDetails?.map((dcd) => {
                                return (
                                    <Typography
                                        key={dcd.index + dcd.label + dcd.value}
                                        variant="body2"
                                        style={{ textAlign: 'start', margin: '0px', color: 'black' }}
                                    >
                                        {(dcd.label ?? '') + ': ' + (dcd.value ?? '')}
                                    </Typography>
                                );
                            })}
                        </div>
                        <div
                            style={{
                                width: '100%',
                                margin: 0,
                            }}
                        >
                            <Typography variant="h6" style={{ textAlign: 'end', margin: '0px', color: 'black' }}>
                                Primalac
                            </Typography>
                            <hr />
                            {documentReceiverTemplate?.map((drt) => {
                                return (
                                    <Typography
                                        key={drt.index + drt.label + drt.value}
                                        variant="body2"
                                        style={{ textAlign: 'end', margin: '0px', color: 'black' }}
                                    >
                                        {(drt.label ?? '') + ': ' + translateClientField(drt.value ?? '')}
                                    </Typography>
                                );
                            })}
                        </div>
                    </div>
                    <Box style={{ margin: 25 }} />

                    <div>
                        <Typography variant="body2" style={{ textAlign: 'start', margin: '0px', color: 'black' }}>
                            Broj: {generateWaybillNumber()}
                        </Typography>
                    </div>

                    <div>
                        <Typography variant="body2" style={{ textAlign: 'start', margin: '0px', color: 'black' }}>
                            Datum: {FormatDateText(new Date())}
                        </Typography>
                    </div>

                    <Typography
                        variant="h3"
                        style={{
                            textAlign: 'center',
                            margin: '0px',
                            color: 'black',
                            marginTop: 15,
                            marginBottom: 15,
                        }}
                    >
                        OTPREMNICA
                    </Typography>
                    <TableContainer>
                        <Table size="small" stickyHeader aria-label="simple table">
                            <TableBody>
                                <TableRow>
                                    <TableCell align="left">Vozilo:</TableCell>
                                    <TableCell align="left">{waybill?.vehicleLicensePlateNum ?? ''}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell align="left">Vozač:</TableCell>
                                    <TableCell align="left">{waybill?.driverName ?? ''}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell align="left">JMBG:</TableCell>
                                    <TableCell align="left">{waybill?.driverUniqueNumber ?? ''}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell align="left">Iz magacina:</TableCell>
                                    <TableCell align="left">
                                        {originSite?.name +
                                            (originSite?.locationInfo?.address
                                                ? ', ' + originSite?.locationInfo?.address
                                                : '')}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell align="left">Na lokaciju:</TableCell>
                                    <TableCell align="left">
                                        {destinationSite?.name +
                                            (destinationSite?.locationInfo?.address
                                                ? ', ' + destinationSite?.locationInfo?.address
                                                : '')}
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Typography
                        variant="h6"
                        style={{
                            textAlign: 'center',
                            margin: '0px',
                            marginBottom: 20,
                            marginTop: '4%',
                        }}
                    >
                        SPISAK ROBE
                    </Typography>
                </div>
            );
        };

        const twItemPages = (): ReactElement => {
            const len = goods?.length;

            return !!len && len > 0 && !!order && !!goods ? (
                <div className="waybill-report-page">
                    <TableContainer>
                        <Table size="small" stickyHeader aria-label="simple table">
                            <TableHead>
                                <TableRow>
                                    <TableCell align="center" sx={{ width: '20%' }}>
                                        Redni br.
                                    </TableCell>
                                    <TableCell align="center" sx={{ width: '60%' }}>
                                        Artikal
                                    </TableCell>
                                    <TableCell align="center" sx={{ width: '20%' }}>
                                        Količina
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {goods.map((good, index) => {
                                    return (
                                        <TableRow
                                            key={
                                                index +
                                                (good.article?.title ?? Math.random().toString()) +
                                                good.quantity
                                            }
                                        >
                                            <TableCell align="center">{index + 1}</TableCell>
                                            <TableCell align="center">{good.article?.title}</TableCell>
                                            <TableCell align="center">{good.quantity}</TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </div>
            ) : (
                <></>
            );
        };

        return !!waybill ? (
            <div ref={ref} className="waybill-report-content-container">
                {pageTitle()}
                {twItemPages()}
                <div
                    style={{
                        position: 'absolute',
                        bottom: 20,
                        width: '80%',
                        textAlign: 'center',
                        fontSize: '20px',
                    }}
                >
                    <div
                        style={{
                            border: '1px solid rgba(0,0,0,0.5)',
                            borderRadius: 5,
                            width: '100%',
                            height: 300,
                            textAlign: 'start',
                            padding: 5,
                            paddingLeft: 15,
                        }}
                    >
                        Napomena
                    </div>
                    <div style={{ height: 150 }}></div>
                    {makeSignatureStampElement()}
                </div>
            </div>
        ) : (
            <></>
        );
    },
);

WaybillReportContent.displayName = 'WaybillReportContent';

export default WaybillReportContent;
